/* oxlint-disable max-lines */
import { or, setup } from 'xstate';

import type { StateActions, StateContext, StateEvents, StateGuards, StateInput } from '../../types';

export const machine = setup({
	/* oxlint-disable typescript/no-unsafe-type-assertion */
	types: {
		context: {} as StateContext,
		events: {} as StateEvents,
		input: {} as StateInput,
	},
	actions: {} as StateActions,
	guards: {} as StateGuards,
	/* oxlint-enable typescript/no-unsafe-type-assertion */
}).createMachine({
	id: 'start-a-petition-recommendations',
	initial: 'intro',
	context: ({ input }) => input,
	states: {
		intro: {
			on: {
				submit: [
					{
						description: 'if user chose the ai driven flow',
						target: 'issue',
						guard: 'isAiDraft',
						actions: ['onSubmit', 'persistDraftType'],
					},
					{
						description: 'if user chose the user driven flow',
						target: 'create',
						guard: 'isUserDraft',
						actions: ['onSubmit', 'persistDraftType'],
					},
				],
			},
		},
		issue: {
			on: {
				back: [{ target: 'intro', actions: ['unsetDraftType'] }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'recommendations',
						actions: ['onSubmit'],
					},
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		recommendations: {
			on: {
				back: [{ target: 'issue' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						description: 'transition to personal-story for ai',
						target: 'personal-story',
						guard: 'isRecommendationsVariantBAiDraft',
						actions: ['onSubmit', 'persistRecommendationsDraftType'],
					},
					{
						description: 'transition to create for user',
						target: 'create',
						guard: 'isRecommendationsVariantBUserDraft',
						actions: ['onSubmit', 'persistRecommendationsDraftType'],
					},
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		'personal-story': {
			on: {
				back: [
					{
						target: 'recommendations',
						actions: ['unsetRecommendationsDraftType'],
					},
				],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						description: 'transition to preview',
						target: 'preview',
						actions: ['onSubmit'],
					},
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		preview: {
			on: {
				back: [{ target: 'personal-story' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						description: 'transition to edit step',
						target: 'edit',
						guard: { type: 'transitionTo', params: { target: 'edit' } },
						actions: ['onSubmit'],
					},
					{
						description: 'transition to location',
						target: 'location',
					},
				],
			},
		},
		edit: {
			on: {
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'preview',
						guard: { type: 'transitionTo', params: { target: 'preview' } },
						actions: [{ type: 'onSubmit' }],
					},
					{
						target: 'location',
						guard: { type: 'transitionTo', params: { target: 'location' } },
						actions: [{ type: 'onSubmit' }],
					},
					{
						target: 'media',
						guard: { type: 'transitionTo', params: { target: 'media' } },
					},
					{
						description: 'default submit if no target specified',
						target: 'location',
					},
				],
			},
		},
		create: {
			on: {
				back: [
					{
						description: "{ draft: { type: 'user_draft' } }",
						target: 'intro',
						guard: 'isUserDraft',
					},
					{
						description: "{ draft: { type: 'recommendations_variant_b_draft_user' } }",
						target: 'recommendations',
						guard: 'isRecommendationsVariantBUserDraft',
						actions: ['unsetRecommendationsDraftType'],
					},
				],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'optional-preview',
						guard: { type: 'transitionTo', params: { target: 'optional-preview' } },
					},
					{
						target: 'location',
						guard: { type: 'transitionTo', params: { target: 'location' } },
						actions: [{ type: 'onSubmit' }],
					},
					{
						target: 'media',
						guard: { type: 'transitionTo', params: { target: 'media' } },
					},
					{ target: 'location' },
				],
			},
		},
		'optional-preview': {
			on: {
				back: [{ target: 'create' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'create',
						guard: { type: 'transitionTo', params: { target: 'create' } },
					},
					{ target: 'location' },
				],
			},
		},
		media: {
			on: {
				back: [
					{
						description: 'go back to edit',
						target: 'edit',
						guard: or(['isRecommendationsVariantBAiDraft', 'isAiDraft']),
					},
					{
						description: 'go back to create',
						target: 'create',
						guard: or(['isRecommendationsVariantBUserDraft', 'isUserDraft']),
					},
				],
				submit: [
					{
						description: 'go to edit',
						target: 'edit',
						guard: or(['isRecommendationsVariantBAiDraft', 'isAiDraft']),
						actions: ['onSubmit'],
					},
					{
						description: 'go to create',
						target: 'create',
						guard: or(['isRecommendationsVariantBUserDraft', 'isUserDraft']),
						actions: ['onSubmit'],
					},
				],
			},
		},
		location: {
			on: {
				back: [
					{
						description: 'go back to preview',
						target: 'preview',
						guard: 'isRecommendationsVariantBAiDraft',
					},
					{
						description: 'go back to create',
						target: 'create',
						guard: 'isRecommendationsVariantBUserDraft',
					},
				],
				submit: [
					{
						target: 'prepublish',
						actions: ['onSubmit'],
					},
				],
			},
		},
		prepublish: {
			on: {
				back: { target: 'location' },
			},
		},

		// TODO: move to their own machine
		'chat-app-entry': {
			on: {
				back: [{ target: 'issue' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'preview', actions: ['onSubmit'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		social: {
			on: {
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'preview', actions: ['onSubmit'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
	},
});
